<template>
    <div class="sidebar-left mt-2">
        <div class="sidebar">
            <div :class="{ show: mqShallShowLeftSidebar }" class="sidebar-content">
                <div id="team-chat-filters-container" class="d-flex mb-2">
                    <div v-if="!createForm && !editChat">
                        <div class="search-wrapper">
                            <b-input-group class="input-group-merge w-100 mr-75 round">
                                <b-form-input v-model="searchQuery" :placeholder="$t('Search')" />
                                <b-input-group-append is-text>
                                    <div class="saysimple-icon search" />
                                </b-input-group-append>
                            </b-input-group>

                            <b-form-checkbox
                                v-model="isReversed"
                                v-b-tooltip.hover.top="$t('sort')"
                                button
                                button-variant="light-alt"
                                class="mr-50 btn-square round-medium"
                                @click="toggleReversedList"
                            >
                                <font-awesome-icon icon="arrow-down-big-small" />
                            </b-form-checkbox>

                            <b-button variant="primary" @click="toggleCreateTeamChat">
                                {{ $t("team-chats.create-chat") }}
                            </b-button>
                        </div>

                        <!-- Sidebar close icon -->
                        <span class="sidebar-close-icon">
                            <font-awesome-icon
                                icon="xmark"
                                @click="$emit('update:mq-shall-show-left-sidebar', false)"
                            />
                        </span>
                    </div>

                    <div v-else>
                        <p>
                            <b-link class="router-link-active" @click="toggleCreateTeamChat">
                                <font-awesome-icon icon="arrow-left" />
                                {{ $t("team-chats.back-to-chat") }}
                            </b-link>
                        </p>

                        <h3>{{ isEdit ? $t("team-chats.edit-with-team") : $t("team-chats.create-with-team") }}</h3>

                        <hr />

                        <b-tabs v-if="!isEdit" pills>
                            <b-tab
                                :active="chatType === 'one_to_one'"
                                :title="$t('team-chats.new-chat')"
                                @click="chatType = 'one_to_one'"
                            />

                            <b-tab
                                :active="chatType === 'group'"
                                :title="$t('team-chats.new-group')"
                                @click="chatType = 'group'"
                            />
                        </b-tabs>

                        <b-input-group v-if="chatType === 'one_to_one'" class="input-group-merge w-100 mr-1 round">
                            <b-form-input v-model="searchQuery" :placeholder="$t('Search')" />
                            <b-input-group-append is-text>
                                <div class="saysimple-icon search" />
                            </b-input-group-append>
                        </b-input-group>

                        <team-chat-create-group-chat
                            v-if="chatType === 'group'"
                            :edit-chat="editChat"
                            :selected-users="selectedUsers"
                            :users="users"
                            @set-edited-chat="setEditedChat"
                            @remove-selected-user="removeSelectedUser"
                            @remove-selected-users="removeSelectedUsers"
                            @toggle-create-team-chat="toggleCreateTeamChat"
                        />
                    </div>
                </div>
                <team-chat-left-sidebar-chats
                    v-if="!createForm && !editChat"
                    :chats="filteredAndSortedChatList"
                    :is-fetching-chats="isFetchingChats"
                    :user="user"
                    @toggle-create-team-chat="toggleCreateTeamChat"
                    @team-chat-clicked="teamChatClicked"
                    @delete-chat="deleteChat"
                    @leave-chat="leaveChat"
                    @set-status="setStatus"
                />

                <div
                    v-if="createForm && chatType === 'one_to_one'"
                    class="team-chat-user-list-wrapper list-group scroll-area scroll-container w-100"
                >
                    <div class="team-chat-user-list">
                        <team-user-item
                            v-for="user of filteredList"
                            :key="user.id"
                            :has-start="true"
                            :user="user"
                            @toggle-create-team-chat="toggleCreateTeamChat"
                        />
                    </div>
                </div>

                <team-chat-users-modal :users="usersTeamsFiltered" @add-selected-users="addSelectedUsers" />
            </div>
        </div>
    </div>
</template>

<script>
import {
    BAvatar,
    BBadge,
    BButton,
    BCol,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormRadioGroup,
    BImg,
    BInputGroup,
    BInputGroupAppend,
    BLink,
    BNav,
    BNavItem,
    BOverlay,
    BRow,
    BTab,
    BTabs,
    VBTooltip,
} from "bootstrap-vue";
import VueContext from "vue-context";
import { computed, onMounted, onUnmounted, ref, watch } from "@vue/composition-api";
import vSelect from "vue-select";
import useUsers from "@/composables/useUsers";
import TeamUserItem from "./teamUserItem.vue";
import TeamChatCreateGroupChat from "./teamChatCreateGroupChat.vue";
import TeamChatUsersModal from "../teamChatUsersModal.vue";
import TeamChatLeftSidebarChats from "./teamChatLeftSidebarChats.vue";
import { getUserData } from "@/auth/getUserData";
import useTeamChat from "../useTeamChat";
import useActiveTeamChat from "../useActiveTeamChat";
import { useToast } from "vue-toastification/composition";
import i18n from "@/libs/i18n";

export default {
    components: {
        BNav,
        BNavItem,
        BAvatar,
        BInputGroup,
        BInputGroupAppend,
        BFormRadioGroup,
        BFormInput,
        BFormCheckbox,
        BButton,
        BImg,
        BLink,
        BFormGroup,
        BRow,
        BCol,
        BOverlay,
        BBadge,
        VueContext,
        vSelect,
        BTab,
        BTabs,
        TeamUserItem,
        TeamChatCreateGroupChat,
        TeamChatUsersModal,
        TeamChatLeftSidebarChats,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    props: {
        createForm: Boolean,
        editChat: Number,
        mqShallShowLeftSidebar: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            isReversed: false,
        };
    },
    computed: {
        filteredList() {
            return this.users.filter((user) => {
                return user.name.toLowerCase().includes(this.searchQuery.toLowerCase());
            });
        },
        filteredAndSortedChatList() {
            const filteredList = this.chats.filter((chat) => {
                return (
                    chat.name?.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                    chat.participants.some((participant) =>
                        participant.user?.name.toLowerCase().includes(this.searchQuery.toLowerCase())
                    )
                );
            });

            const sortedList = filteredList.sort((a, b) => {
                if (a.lastMessage?.createdAt && b.lastMessage?.createdAt) {
                    if (b?.isArchived === a?.isArchived) {
                        return new Date(b.lastMessage?.createdAt) - new Date(a.lastMessage?.createdAt);
                    } else {
                        return b?.isArchived < a?.isArchived ? 1 : -1;
                    }
                }

                if (!this.isReversed) {
                    if (b?.isArchived === a?.isArchived) {
                        return a.lastMessage?.createdAt ? -1 : 1;
                    } else {
                        return b?.isArchived < a?.isArchived ? 1 : -1;
                    }
                }
                if (b?.isArchived === a?.isArchived) {
                    return a.lastMessage?.createdAt ? 1 : -1;
                } else {
                    return b?.isArchived < a?.isArchived ? 1 : -1;
                }
            });
            if (this.isReversed) {
                return sortedList.reverse().sort((a, b) => {
                    if (b?.isArchived === a?.isArchived) {
                        return 0;
                    } else {
                        return b?.isArchived < a?.isArchived ? 1 : -1;
                    }
                });
            }
            return sortedList;
        },
        user() {
            return getUserData();
        },
    },
    methods: {
        toggleReversedList() {
            this.isReversed = !this.isReversed;
        },
        addSelectedUsers(list) {
            this.selectedUsers = [...this.selectedUsers, ...list];
        },
        removeSelectedUsers() {
            this.selectedUsers = [];
        },
        removeSelectedUser(id) {
            this.selectedUsers = this.selectedUsers.filter((userId) => userId !== id);
        },
        teamChatClicked(obj) {
            this.$emit("team-chat-clicked", { id: obj.id });
            this.$root.$router.push(`/team-chat/${obj.id}`);
        },
        toggleCreateTeamChat() {
            this.searchQuery = "";
            this.$emit("toggle-create-team-chat");
        },
        setEditedChat(id) {
            this.$emit("set-edited-chat", id);
        },
        deleteChat(obj) {
            this.$bvModal
                .msgBoxConfirm(this.$t("team-chats.confirm.delete-text"), {
                    title: this.$t("team-chats.confirm.delete-text"),
                    cancelTitle: this.$t("team-chats.confirm.cancel"),
                    cancelVariant: "light",
                    okTitle: this.$t("team-chats.confirm.delete"),
                    hideHeaderClose: false,
                    headerCloseVariant: "light",
                    centered: true,
                })
                .then((result) => {
                    if (result) {
                        this.deleteTeamChat(obj.chatId).then(() => {
                            if (this.activeChat && obj.chatId === this.activeChat.id) {
                                router.replace({
                                    name: "team-chat",
                                });
                            }
                        });
                    }
                });
        },
        leaveChat(obj) {
            this.$bvModal
                .msgBoxConfirm(this.$t("team-chats.confirm.leave-text"), {
                    title: this.$t("team-chats.confirm.leave-title"),
                    cancelTitle: this.$t("team-chats.confirm.cancel"),
                    cancelVariant: "light",
                    okTitle: this.$t("team-chats.confirm.leave"),
                    hideHeaderClose: false,
                    headerCloseVariant: "light",
                    centered: true,
                })
                .then((result) => {
                    if (result) {
                        this.leaveTeamChat(obj.chatId, obj.participantId).then(() => {
                            if (this.activeChat && obj.chatId === this.activeChat.id) {
                                const { leaveTeamChat } = useActiveTeamChat();

                                router.replace({
                                    name: "team-chat",
                                });

                                leaveTeamChat();
                            }
                        });
                    }
                });
        },
        setStatus(obj) {
            this.setParticipantChatStatus(obj.chatId, obj.participantId, obj.chatStatus);
        },
    },
    emits: ["toggle-create-team-chat", "team-chat-clicked", "set-edited-chat"],
    setup(props, context) {
        const { users, teams, fetchUsers, fetchTeams } = useUsers();
        const { leaveTeamChat, deleteTeamChat, fetchChats, isFetchingChats, chats, setParticipantChatStatus } =
            useTeamChat();
        const { activeChat, resetActiveChat } = useActiveTeamChat();

        const searchQuery = ref("");
        const isEdit = computed(() => Boolean(props.editChat));
        const chatType = ref(isEdit.value ? "group" : "one_to_one");
        const selectedUsers = ref([]);
        const toast = useToast();

        const updateParticipants = () => {
            if (isEdit.value && Array.isArray(activeChat.value.participants)) {
                selectedUsers.value = activeChat.value.participants.reduce((ids, participant) => {
                    if (!participant.leftAt) {
                        ids.push(participant.user.id);
                    }
                    return ids;
                }, []);
            } else {
                selectedUsers.value = [];
            }
        };

        watch(isEdit, () => {
            if (isEdit.value) {
                chatType.value = "group";
            } else {
                chatType.value = "one_to_one";
            }
            updateParticipants();
        });

        watch(activeChat, (currentValue, oldValue) => {
            if (
                currentValue?.value?.participants?.map((participant) => participant.id).toString() !==
                oldValue?.value?.participants?.map((participant) => participant.id).toString()
            ) {
                updateParticipants();
            }
        });

        onMounted(() => {
            Promise.all([
                fetchUsers({
                    s: {
                        $and: [
                            { role: { $ne: "API" } },
                            { role: { $ne: "saysimple" } },
                            { active: { $eq: 1 } },
                            { id: { $ne: getUserData().id } },
                        ],
                    },
                }),
                fetchTeams({
                    fields: "id,name,color",
                    join: "teamUsers||userId",
                }),
                //                 const authService = getInstance();
                // authService.$watch("loading", (isLoading) => {
                //     if (isLoading === false) {
                //         return fetchChats();
                //     }
                // });
                fetchChats(),
            ]).catch(() => {
                toast(i18n.t("team-chats.failure.fetch-data"), {
                    title: i18n.t("team-chats.failure.fetch-data-title"),
                    variant: "danger",
                });
            });
        });
        onUnmounted(() => {
            resetActiveChat();
        });
        const usersTeamsFiltered = computed(() => users.value.filter((user) => !selectedUsers.value.includes(user.id)));

        return {
            searchQuery,
            users,
            teams,
            chatType,
            selectedUsers,
            usersTeamsFiltered,
            fetchUsers,
            toast,
            isFetchingChats,
            chats,
            isEdit,
            setParticipantChatStatus,
            leaveTeamChat,
            deleteTeamChat,
        };
    },
};
</script>
